import { SiteType } from '@/api/tracking';
import dayjs from 'dayjs';
import {
  AdpDocument,
  AdpDocumentBasicInfo,
  DocType,
  isArticle,
  isPatent,
  isReport,
  isResearchPaper,
  UserDocument,
} from '@/types';

export const docTypeDisplayNameMap: Record<DocType, string> = {
  article: '記事',
  research_paper: '論文',
  patent: '特許',
  report: 'レポート',
};

export const getDocTypeDisplayName = (adpDocument?: AdpDocumentBasicInfo) =>
  adpDocument ? docTypeDisplayNameMap[adpDocument.doc_type] : '';

export const getPublisher = (adpDocument?: AdpDocumentBasicInfo) => {
  if (isArticle(adpDocument)) {
    return adpDocument.media_name;
  } else if (isResearchPaper(adpDocument)) {
    return adpDocument.journal_name;
  } else if (isPatent(adpDocument)) {
    return adpDocument.applicant;
  } else if (isReport(adpDocument)) {
    return adpDocument.site_name;
  }
  return '';
};

export const getPageUrl = (
  adpDocument: AdpDocumentBasicInfo,
  usePdfUrl: boolean,
) => {
  switch (adpDocument.doc_type) {
    case 'article':
    case 'patent':
    case 'research_paper':
      return hasPdfUrl(adpDocument) && usePdfUrl && adpDocument.pdf_url
        ? adpDocument.pdf_url
        : adpDocument.url;
    case 'report':
      return `${adpDocument.url}#page=${
        isReport(adpDocument) ? adpDocument.page_number ?? 1 : 1
      }`;
    default:
      return '';
  }
};

export const buildRedirectUrl = (
  docType: string,
  articleId: string,
  queryString: string | undefined,
): string =>
  `/api/v1/redirect/${docType}/${articleId}${queryString ? `?${queryString}` : ''}`;

export const getPatentPublicationOffice = (
  adpDocument?: AdpDocumentBasicInfo,
): string | undefined => {
  if (isPatent(adpDocument) && adpDocument.publication_office) {
    return adpDocument.publication_office.toUpperCase();
  }
  return undefined;
};

export const getPatentIdentifier = (
  adpDocument?: AdpDocumentBasicInfo,
): string | undefined => {
  if (isPatent(adpDocument) && adpDocument.patent_identifier) {
    return adpDocument.patent_identifier;
  }
  return undefined;
};

export const hasPdfUrl = (adpDocument?: AdpDocumentBasicInfo) => {
  switch (adpDocument?.doc_type) {
    case 'article':
    case 'patent':
      return false;
    case 'research_paper':
      return adpDocument.pdf_url !== undefined && adpDocument.pdf_url !== null;
    case 'report':
      return adpDocument.url.includes('.pdf');
    default:
      return true;
  }
};

// タイトル横に「PDF」ラベルを表示すべきか。リンク先を決める hasPdfUrl とは意味が異なり、
// article/patent でも pdf_url があれば PDF とみなす。
export const isPdfDocument = (adpDocument?: AdpDocumentBasicInfo) => {
  switch (adpDocument?.doc_type) {
    case 'article':
    case 'patent':
    case 'research_paper':
      return !!adpDocument.pdf_url;
    case 'report':
      return adpDocument.url.endsWith('.pdf');
    default:
      return false;
  }
};

export const getSiteType = (
  adpDocument?: AdpDocumentBasicInfo,
  usePdfUrl: boolean = true,
): SiteType | undefined => {
  return hasPdfUrl(adpDocument)
    ? usePdfUrl
      ? 'pdf_direct'
      : 'provider'
    : undefined;
};

export const isAllianceMedia = (adpDocument?: AdpDocumentBasicInfo) => {
  if (!adpDocument) return false;
  return 'is_alliance_media' in adpDocument && adpDocument.is_alliance_media;
};

export const getHighlightKeywords = (document: {
  hit_keywords?: string[];
  synonym_keywords?: string[];
}): string[] =>
  [...(document.hit_keywords ?? []), ...(document.synonym_keywords ?? [])]
    .filter(keyword => keyword.length > 0)
    .sort((left, right) => right.length - left.length);

export const isNewArticle = (adpDocument?: AdpDocumentBasicInfo) => {
  if (!adpDocument?.published_at) return false;
  const published = dayjs(adpDocument.published_at);
  return (
    published.isSame(dayjs(), 'day') ||
    published.isSame(dayjs().subtract(1, 'day'), 'day')
  );
};

export const documentKey = (doc: AdpDocument | UserDocument) =>
  `${doc.doc_type}-${doc.id}-${doc.lang}-${isPatent(doc) ? doc.publication_office : ''}-${isReport(doc) ? doc.page_number ?? 1 : ''}`;

export const syncAdpDocumentActions = (
  documents: AdpDocument[],
  updated: AdpDocument,
) => {
  documents
    .filter(doc => documentKey(doc) === documentKey(updated))
    .forEach(doc => {
      doc.marks = updated.marks;
      doc.group_marks = updated.group_marks;
      doc.comments = updated.comments;
      if (updated.is_read) doc.is_read = true;
    });
};
